import { useMemo } from 'react'
import {
  analisarAncoragensBasicas,
  folgaDoTrecho,
  localizarPontoNoMundo,
  pontosDoTrecho,
  type AnaliseAncoragemBasica,
  type CenaTabletop,
  type VetorTabletop,
} from '../../dominio/tabletop'
import { Rotulo3D } from '../componentes/Rotulo3D'

const acima = (p: VetorTabletop, dy: number): [number, number, number] => [p[0], p[1] + dy, p[2]]

/** O ponto mais baixo de um trecho, pendurado com a folga dele: onde fica o rótulo da catenária e da folga de tensão. */
function fundoDoTrecho(cena: CenaTabletop, analise: AnaliseAncoragemBasica, indice: number): VetorTabletop | null {
  const corda = cena.cordas?.find((c) => c.id === analise.idCorda)
  if (!corda) return null
  const de = localizarPontoNoMundo(cena, corda.percurso[indice]!)
  const para = localizarPontoNoMundo(cena, corda.percurso[indice + 1]!)
  if (!de || !para) return null
  return pontosDoTrecho(de, para, folgaDoTrecho(corda, indice)).reduce((baixo, p) => (p[1] < baixo[1] ? p : baixo))
}

function Rotulos({ cena, analise }: { cena: CenaTabletop; analise: AnaliseAncoragemBasica }) {
  const { posicoes, fusivel } = analise
  const catenaria = fundoDoTrecho(cena, analise, 0)
  const folgaDeTensao = fusivel ? fundoDoTrecho(cena, analise, 1) : null
  const meioDoFusivel =
    fusivel && posicoes.noDoFusivel
      ? ([
          (posicoes.primario[0] + posicoes.noDoFusivel[0]) / 2,
          (posicoes.primario[1] + posicoes.noDoFusivel[1]) / 2,
          (posicoes.primario[2] + posicoes.noDoFusivel[2]) / 2,
        ] as VetorTabletop)
      : null
  return (
    <group name={`ancoragem-basica-${analise.idCorda}`}>
      <Rotulo3D posicao={acima(posicoes.secundario, 0.3)} texto="Ponto secundário" variante="estrutura" sempreVisivel fator={5} />
      <Rotulo3D posicao={acima(posicoes.primario, 0.3)} texto="Ponto primário" variante="estrutura" sempreVisivel fator={5} />
      {catenaria && (
        <Rotulo3D
          posicao={acima(catenaria, -0.2)}
          texto="Catenária"
          subtexto={analise.catenariaEsticada ? 'sem folga: divide a carga' : undefined}
          variante={analise.catenariaEsticada ? 'risco' : 'neutro'}
          sempreVisivel
          fator={5}
        />
      )}
      {folgaDeTensao && <Rotulo3D posicao={acima(folgaDeTensao, -0.2)} texto="Folga de tensão" variante="neutro" sempreVisivel fator={5} />}
      {meioDoFusivel && <Rotulo3D posicao={[meioDoFusivel[0] + 0.35, meioDoFusivel[1], meioDoFusivel[2]]} texto="Fusível" variante="neutro" sempreVisivel fator={5} />}
    </group>
  )
}

/**
 * Os rótulos da ancoragem básica na cena, como na figura de referência: ponto
 * secundário, catenária, ponto primário, folga de tensão e fusível. Aparecem
 * sozinhos para cada corda que o estudo reconhece (ver `ancoragemBasica.ts`).
 */
export function AncoragemBasica3D({ objetos, cordas }: { objetos: CenaTabletop['objetos']; cordas: NonNullable<CenaTabletop['cordas']> }) {
  const cena = useMemo<CenaTabletop>(() => ({ versao: 2, nome: '', objetos, cordas }), [objetos, cordas])
  const analises = useMemo(() => analisarAncoragensBasicas(cena), [cena])
  return (
    <>
      {analises.map((a) => (
        <Rotulos key={a.idCorda} cena={cena} analise={a} />
      ))}
    </>
  )
}
